/**
 * MobileBottomNav — 5-tab bottom navigation for mobile viewports.
 *
 * Industry-standard iGaming pattern (BC.Game / Stake / 1win / Roobet):
 *   Home · Games · [DEPOSIT — elevated] · Promos · Menu
 *
 * Rules:
 *   - Visible only < lg (1024px). This matches the breakpoint the top-nav
 *     hamburger uses (`lg:hidden`), so the bottom nav appears whenever the
 *     sidebar is drawer-mode.
 *   - Hidden on `fullBleed` layouts (the immersive `/game/:slug` page).
 *   - Menu tab does NOT create a new drawer — it calls the same
 *     `onOpenMenu` callback the top hamburger uses, so state stays in
 *     `AppShell` (`mobileOpen` / `setMobileOpen`).
 *   - Deposit tab routes unauthed users to `/login?next=/wallet/deposit`
 *     via a lightweight `useAuth()` check.
 *   - Safe-area inset padding on the bar itself; the corresponding
 *     content-bottom offset lives in `AppShell` `<main>` so we don't have
 *     to touch every page.
 */
import { Gamepad2, Gift, Home, LogIn, Menu, Trophy, UserPlus, Wallet } from "lucide-react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../../context/AuthContext";
import ActiveBonusBadge from "../ActiveBonusBadge";
import BetaBadge from "../BetaBadge";

// Public tabs (visible to anonymous visitors too).
const PUBLIC_TABS_LEFT = [
  { key: "home", label: "Home", icon: Home, to: "/", testid: "mnav-home" },
  { key: "games", label: "Games", icon: Gamepad2, to: "/#games", testid: "mnav-games" },
];

// Auth-only tabs. `Sports` requires a JWT-authed SABA session launch;
// `Promos` routes to /promotions which is a `ProtectedRoute`. Hidden
// from anonymous visitors — the sidebar drawer + topbar carry the
// login/signup CTAs so anon users still have a discoverable path.
const AUTH_TABS_LEFT = [
  { key: "sports", label: "Sports", icon: Trophy, to: "/sportsbook", testid: "mnav-sports" },
];
const AUTH_TABS_RIGHT = [
  { key: "promos", label: "Promos", icon: Gift, to: "/promotions", testid: "mnav-promos" },
];

export default function MobileBottomNav({ onOpenMenu, unreadCount = 0 }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();

  // Anon layout collapses down to Home · Games · [Sign up CTA] · Log in · Menu.
  // The elevated pink pill remains the visual anchor either way; only its
  // destination + label change, so nothing about the layout shifts.
  const leftTabs = user ? [...PUBLIC_TABS_LEFT, ...AUTH_TABS_LEFT] : PUBLIC_TABS_LEFT;
  const rightTabs = user ? AUTH_TABS_RIGHT : [];

  const depositTarget = user
    ? `/wallet/deposit?currency=${user.preferred_currency || "USDT"}`
    : "/signup";

  const onCenterCta = () => nav(depositTarget);

  // Active state: the Deposit pill is "active" when we're on /wallet/deposit.
  const depositActive = user && loc.pathname === "/wallet/deposit";

  return (
    <nav
      // Bar is `lg:hidden` (same breakpoint the top hamburger uses).
      // Fixed to bottom, above safe-area on iOS notch phones.
      className="lg:hidden fixed inset-x-0 bottom-0 z-40 bg-elevated/95 backdrop-blur border-t border-line shadow-[0_-8px_24px_rgba(0,0,0,0.35)]"
      style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="mobile-bottom-nav"
      aria-label="Primary"
    >
      <div className="flex items-stretch justify-between h-16 px-1">
        {leftTabs.map((t) => (
          <TabLink key={t.key} tab={t} />
        ))}

        {/* Elevated center CTA — Deposit for authed users, Sign up for anon.
            Same visual anchor either way; only label + destination change. */}
        <button
          type="button"
          onClick={onCenterCta}
          className="flex-1 flex flex-col items-center justify-end pb-1.5 min-w-0 select-none"
          style={{ touchAction: "manipulation" }}
          data-testid={user ? "mnav-deposit" : "mnav-signup"}
          aria-current={depositActive ? "page" : undefined}
        >
          <span
            className={
              "relative -mt-4 mb-1 inline-flex items-center justify-center w-14 h-14 rounded-full " +
              "bg-brand-gradient shadow-glow-pink active:scale-95 transition"
            }
          >
            {user
              ? <Wallet className="w-6 h-6 text-white" />
              : <UserPlus className="w-6 h-6 text-white" />}
          </span>
          <span
            className={
              "text-[10px] tracking-widest uppercase font-semibold " +
              (depositActive ? "text-pink" : "text-ink-2")
            }
          >
            {user ? "Deposit" : "Sign up"}
          </span>
        </button>

        {rightTabs.map((t) => (
          <TabLink key={t.key} tab={t} />
        ))}

        {/* Anon-only quick Log in tab — keeps the pair (sign-up + log-in)
            reachable without opening the drawer. Authed users see Menu
            here instead. */}
        {!user && (
          <NavLink
            to="/login"
            className={({ isActive }) =>
              "flex-1 flex flex-col items-center justify-center min-w-0 select-none transition " +
              (isActive ? "text-pink" : "text-ink-2 hover:text-ink active:text-ink")
            }
            style={{ touchAction: "manipulation" }}
            data-testid="mnav-login"
          >
            <LogIn className="w-5 h-5" />
            <span className="mt-1 text-[10px] tracking-widest uppercase font-semibold">Log in</span>
          </NavLink>
        )}

        {/* Menu — opens the same drawer as the top hamburger */}
        <button
          type="button"
          onClick={onOpenMenu}
          className="flex-1 flex flex-col items-center justify-center min-w-0 text-ink-2 hover:text-ink active:text-ink transition select-none relative"
          style={{ touchAction: "manipulation" }}
          data-testid="mnav-menu"
          aria-label="Open menu"
        >
          <span className="relative">
            <Menu className="w-5 h-5" />
            {unreadCount > 0 && (
              <span
                className="absolute -top-0.5 -right-1 w-2 h-2 rounded-full bg-pink shadow-glow-pink"
                data-testid="mnav-menu-dot"
              />
            )}
          </span>
          <span className="mt-1 text-[10px] tracking-widest uppercase font-semibold">Menu</span>
        </button>
      </div>
    </nav>
  );
}

function TabLink({ tab }) {
  const Icon = tab.icon;
  // Games routes to `/#games` — anchor on Landing. Treat it as "active" only
  // when the user is actually on Landing (path === "/"), not everywhere.
  const isAnchor = tab.to.includes("#");
  const to = tab.to;
  return (
    <NavLink
      to={to}
      end={tab.to === "/"}
      className={({ isActive }) => {
        const on = isAnchor ? false : isActive;
        return (
          "flex-1 flex flex-col items-center justify-center min-w-0 select-none transition " +
          (on ? "text-pink" : "text-ink-2 hover:text-ink active:text-ink")
        );
      }}
      style={{ touchAction: "manipulation" }}
      data-testid={tab.testid}
    >
      <span className="relative">
        <Icon className="w-5 h-5" />
        {/* Only the Promos tab wears the active-bonus badge. The <lg
            breakpoint AppShell hides the desktop promo pill, so this is
            the only surface where mobile users can see unmet-wagering
            counts without opening the drawer. */}
        {tab.key === "promos" && (
          <ActiveBonusBadge
            className="absolute -top-1.5 -right-2 min-w-[16px] h-4 px-1 rounded-full bg-gold text-black text-[9px] font-bold flex items-center justify-center shadow-glow-gold"
            testid="mnav-promos-badge"
          />
        )}
        {/* Sports is beta-scaffolded until real SABA credentials land;
            reuse the same BetaBadge as the desktop pill so the two
            surfaces stay visually consistent. */}
        {tab.key === "sports" && (
          <BetaBadge className="absolute -top-2 -right-3" />
        )}
      </span>
      <span className="mt-1 text-[10px] tracking-widest uppercase font-semibold">{tab.label}</span>
    </NavLink>
  );
}
